import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const Calendar = ({ logs, selectedDate, onDateSelect }) => {
  const { t, i18n } = useTranslation()
  const [currentDate, setCurrentDate] = useState(new Date(selectedDate))

  // Update calendar view when selectedDate changes externally
  useEffect(() => {
    setCurrentDate(new Date(selectedDate))
  }, [selectedDate])

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDayOfMonth = new Date(year, month, 1).getDay() // 0 = Sunday

  // Monday start logic: 0(Sun)->6, 1(Mon)->0, ...
  const startingDayIndex = (firstDayOfMonth + 6) % 7

  // 2024/1/1は月曜。月曜始まりの曜日ラベルをロケールから生成
  const weekDays = [...Array(7)].map((_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(i18n.language, { weekday: 'short' })
  )

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  // Get active dates from logs (unique dates) where workout_sets exist
  const activeDates = new Set(logs.filter(log => log.workout_sets && log.workout_sets.length > 0).map(log => log.date))

  const handleDateClick = (day) => {
    // Format: YYYY-MM-DD
    // Use local time construction to avoid timezone shifts
    const clickedDate = new Date(year, month, day)
    // Offset for local timezone to ensure YYYY-MM-DD matches local day
    const offset = clickedDate.getTimezoneOffset() * 60000
    const localDate = new Date(clickedDate.getTime() - offset)
    const dateString = localDate.toISOString().split('T')[0]

    onDateSelect(dateString)
  }

  const renderDays = () => {
    const days = []

    // Empty cells for previous month
    for (let i = 0; i < startingDayIndex; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>)
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStringYMD = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const isSelected = selectedDate === dateStringYMD
      const hasTraining = activeDates.has(dateStringYMD)
      const isToday = dateStringYMD === new Date().toISOString().split('T')[0]

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`
            h-10 w-10 flex flex-col items-center justify-center rounded-full relative transition-colors
            ${isSelected ? 'bg-orange-500 text-black font-bold' : 'text-white hover:bg-[#2c2c2e]'}
            ${isToday && !isSelected ? 'border border-orange-500' : ''}
          `}
        >
          <span className="text-sm">{day}</span>
          {hasTraining && !isSelected && (
            <span className="absolute bottom-1 w-1 h-1 bg-orange-500 rounded-full"></span>
          )}
          {hasTraining && isSelected && (
            <span className="absolute bottom-1 w-1 h-1 bg-black rounded-full"></span>
          )}
        </button>
      )
    }

    return days
  }

  return (
    <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="text-gray-400 hover:text-white p-1" aria-label={t('previous_month')}>
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h3 className="text-lg font-bold text-white">
          {currentDate.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long' })}
        </h3>
        <button onClick={nextMonth} className="text-gray-400 hover:text-white p-1" aria-label={t('next_month')}>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {weekDays.map((d, i) => (
          <div key={i} className={`text-xs ${i >= 5 ? 'text-gray-500' : 'text-gray-400'}`}>
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 place-items-center">
        {renderDays()}
      </div>
    </div>
  )
}

export default Calendar
